import { zodResolver } from "@hookform/resolvers/zod"
import { Box, TextField, Typography } from "@mui/material"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { PricingNumericTypeValues } from "../../constants"
import NumericTypeField from "../forms/NumericTypeField"

import { Button } from "@/components/common/Button"
import { FormNumericField, FormSelect } from "@/components/UiKit"
import type { PriceChangeItems } from "@/graphql/codegen/graphql"
import {
  type WarehouseHeaderFormValues,
  warehouseHeaderFormSchema,
} from "@/screens/Pricing/schema/WarehouseHeaderSchema"
import type { Option } from "@/types/form-fields"
import { formatNumber } from "@/utils/number"
import { formatLocationAddress } from "@/utils/utils"

interface PricingWarehouseHeaderProps {
  priceChangeItems: PriceChangeItems
  incotermsOptions: Option<string>[]
  incotermsLocationOptions: Option<string>[]
  onApplySelections: (values: WarehouseHeaderFormValues) => void
}

export const PricingWarehouseHeader: FC<PricingWarehouseHeaderProps> = ({
  priceChangeItems,
  incotermsOptions,
  incotermsLocationOptions,
  onApplySelections,
}) => {
  const methods = useForm<WarehouseHeaderFormValues>({
    resolver: zodResolver(warehouseHeaderFormSchema),
    defaultValues: {
      newIncoterms: "",
      newIncotermsLocation: "",
      desiredPriceChange: null,
      priceChangeMode: PricingNumericTypeValues.Percent,
    },
  })

  const { control, handleSubmit } = methods

  const { shipFromWarehouseArea, incoterms, incotermsLocation, lastLaidDownCost, acceptedCost } = priceChangeItems
  const incotermsLocationLabel = formatLocationAddress(incotermsLocation ?? null) ?? ""

  const handleApplySelections = (values: WarehouseHeaderFormValues) => {
    onApplySelections(values)
  }

  return (
    <FormProvider {...methods}>
      <Box
        component='form'
        onSubmit={handleSubmit(handleApplySelections)}
        className='flex items-start gap-3 border border-solid border-gray-400 bg-primary-100 px-6 py-4'
      >
        <Box>
          <Typography variant='body2' className='text-gray-700'>
            Ship From:
          </Typography>
          <Typography variant='body2' className='text-gray-700'>
            {formatLocationAddress(shipFromWarehouseArea?.location ?? null)}
          </Typography>
        </Box>

        <TextField label='Current Incoterms' value={incoterms} disabled size='small' className='w-[168px]' />

        <TextField
          label='Current Incoterms Location'
          value={incotermsLocationLabel}
          disabled
          size='small'
          className='w-[168px]'
        />

        <FormSelect
          control={control}
          name='newIncoterms'
          label='New Incoterms'
          options={incotermsOptions}
          size='small'
          className='w-[168px]'
        />

        <FormSelect
          control={control}
          name='newIncotermsLocation'
          label='New Incoterms Location'
          options={incotermsLocationOptions}
          size='small'
          className='w-[168px]'
        />

        <TextField
          label='Last Laid Down Cost'
          value={`$${formatNumber(lastLaidDownCost!)}`}
          disabled
          size='small'
          className='w-[168px]'
        />

        <TextField
          label='Current Accepted Cost'
          value={`$${formatNumber(acceptedCost!)}`}
          disabled
          size='small'
          className='w-[144px]'
        />

        <Box className='flex items-end gap-3'>
          <FormNumericField
            control={control}
            name='desiredPriceChange'
            label='Desired Price % Change'
            size='small'
            className='w-16'
          />

          <NumericTypeField name='priceChangeMode' />
        </Box>

        <Box className='pt-5'>
          <Button type='submit' appearance='outlined'>
            Apply Selections
          </Button>
        </Box>
      </Box>
    </FormProvider>
  )
}
